import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";
import { CouponService } from "./coupon.service";
import { CoreLogService } from "../../services/log.service";
import { CoreLanguageService } from "../../services/language.service";
import { ActivatedRoute, Router } from "@angular/router";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { ScrollHelper } from "../../shared/scrollHelper";
import { Paging } from "../../shared/paging";
import Utils from "../../shared/utils";
import { CouponViewModel } from "../../shared/models/coupon.model";
import { PagingHelper } from "../../shared/pagingHelper";
import { SortOption } from "../../shared/models/sort.model";
import { SnackBarService, snackBarMessageKey } from "../../services/snackBar.service";
import { LikeService } from "../../services/like.service";
import { ContentType, SortDirection, SnackBarMessageType } from "../../shared/contentTypes";
import { RouterHelperService } from "../../services/routerHelper.service";
import { UserHelperService } from "../../services/userHelper.service";
import { OverlaySpinnerService } from "../overlayspinner/overlaySpiner.service";
import { BottomSheetHelperService } from "../../services/bottomSheetHelper.service";
import { FilterLocationDialogComponent } from "../../modalDialogs/filterLocationDialog/filterLocationDialog.component";
import { animate, query, stagger, style, transition, trigger } from "@angular/animations";
import { MainnavService, ViewType } from "../mainNav/mainnav.service";
import { NavigationTitleViewModel } from "../../shared/models";
import { RatingService } from "../../services/rating.service";
import { plopInOut } from "../../shared/animation/component.animation";
import { WebShopService } from "../../services/webShop.service";
import { CouponRedemptionService } from "../../services/couponRedemption.service";

@Component({
  selector: "coupon",
  templateUrl: "./coupon.component.html",
  styleUrls: ["./coupon.component.scss"],
  providers: [CouponService, OverlaySpinnerService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("couponButtonEntering", [
      transition("false => true", [
        query(".news-button", [
          style({ transform: "scale(0)" }),
          stagger(100, [
            animate("400ms ease-out", style({ transform: "scale(1.1)" })),
            animate("100ms ease-in-out", style({ transform: "scale(1)" }))
          ])
        ])
      ])
    ]),
    plopInOut
  ]
})
export class CouponComponent implements OnInit, OnDestroy {
  /**
   * used for filtering the list of Coupons
   * @param locationFilterIds Ids of locations for filtering the list
   * @result return a function that is used by Paging module for filtering the list
   */
  private static couponLocationFilterFunction(locationFilterIds: number[]): ((item: CouponViewModel) => boolean) {
    return (coupon: CouponViewModel): boolean => {
      return (coupon.locations.some(l => locationFilterIds.includes(l)));
    };
  }
  private static couponLikeFilterFunction(): ((item: CouponViewModel) => boolean) {
    return (coupon: CouponViewModel): boolean => {
      return (coupon.like);
    };
  }

  public contentType = ContentType;
  public isLogin: boolean;
  public hasData: boolean = true; // check if there is data for this page;
  public pageTitle: NavigationTitleViewModel = new NavigationTitleViewModel(); // Used as a title at the header of the page
  // if there is a targetScrollId page must be scrolled to top to show this item at the top of the page
  private targetScrollId: number = 0;
  public couponList: CouponViewModel[];
  public paging: Paging<CouponViewModel>;
  private pagingHelper: PagingHelper;
  private sortOption: SortOption[];
  public locationFilterIds: number[]; // Used as argument for filterfunction.
  private filterFunction: ((item: CouponViewModel) => boolean)[];
  public matGroupSelectedIndex: number = 0;
  public isLoaded: boolean = false;
  public isSmallView: boolean = false;
  private _hasLikeFilter: boolean;
  private _currentLocation: number;
  private _viewTypeSubscription: Subscription;
  private _langSubscriber: Subscription;
  private _ratingChangesSubscriber: Subscription;
  private _infiniteScrollSubscriber: Subscription;
  private _couponChangesSubscriber: Subscription;

  constructor(private couponService: CouponService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _coreLogService: CoreLogService,
    private _coreLanguageService: CoreLanguageService,
    private _ratingService: RatingService,
    private _likeService: LikeService,
    private _activatedRoute: ActivatedRoute,
    private _userHelperService: UserHelperService,
    private internationalisationService: InternationalisationService,
    private _routerHelperService: RouterHelperService,
    private _router: Router,
    private _snackBarService: SnackBarService,
    private _overlaySpinnerService: OverlaySpinnerService,
    private _scrollHelper: ScrollHelper,
    private _bottomSheetHelperService: BottomSheetHelperService,
    private _mainnavService: MainnavService,
    private _webShopService: WebShopService,
    private _couponRedemptionService: CouponRedemptionService) {
  }

  /** Returns the resolved aria-label text for the coupon detail navigation action. */
  public get couponDetailAriaLabel(): string {
    const me = this;
    return me._coreLanguageService.value("phA11Y_open_coupon_detail");
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("CouponComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  /** Routes a coupon-card click to the appropriate dialog or navigation based on coupon state. */
  public couponClickHandler(coupon: CouponViewModel): void {
    const me = this;
    if (coupon.isRedeemed) {
      me._couponRedemptionService.openAlreadyRedeemedDialog(() => me.PushChanges());
      return;
    }
    if (me.isCouponInCart(coupon.id)) {
      me._couponRedemptionService.openInCartDialog(() => me.PushChanges());
      return;
    }
    me.navigateToCouponDetail(coupon.id);
  }

  /** Delegates to the service single-source-of-truth; keeps the template free of direct service access. */
  public isCouponInCart(couponId: number): boolean {
    const me = this;
    return me._couponRedemptionService.isInCart(couponId);
  }

  /** Supports ngFor trackBy — avoids full list re-render on each change-detection cycle. */
  public trackCouponById(_index: number, coupon: CouponViewModel): number {
    return coupon.id;
  }

  ngOnInit(): void {
    const me = this;
    me._currentLocation = me._webShopService.locationID
    me.couponList = [];
    me.pagingHelper = new PagingHelper();
    me.sortOption = [];
    me.filterFunction = [];
    me.locationFilterIds = [];
    me.isLogin = me._userHelperService.IsRegistered;
    me.setRouteData();

    me._langSubscriber = this.internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });

    me._couponChangesSubscriber = me.couponService.ObserveChanges.subscribe((change) => {
      me.ReloadDataAndRender();
    });

    me.onViewTypeChanges(me._mainnavService.viewType);
    me._viewTypeSubscription = me._mainnavService.observeViewTypeChanges.subscribe((viewType) => {
      me.onViewTypeChanges(viewType);
      me.PushChanges();
    });
    me.initializeSortAndFilterOption();
    me.setInfiniteScrollSubscriber();
    me.setRatingSubscriber();
    me.initializeData();
    me.PushChanges();
  }

  /**
   * open Location filter dialog to set the new filter for the coupon list
   */

  public openFilterDialog(): void {
    const me = this;
    const bottomSheetRef = me._bottomSheetHelperService.open(FilterLocationDialogComponent, {
      data: me.locationFilterIds
    });
    // show snackBar after rating Item
    // set filterFunction base on result
    bottomSheetRef.afterDismissed().subscribe((result: number[]) => {
      if (result) {
        me.filterFunction = [];
        me.locationFilterIds = result;
        if (me.locationFilterIds && me.locationFilterIds.length > 0) {
          me.filterFunction.push(CouponComponent.couponLocationFilterFunction(me.locationFilterIds));
        }
        me.paging.setFilter(me.filterFunction);
        me.paging.setSorting(me.sortOption);
        me.couponList = []; // clean up
        me.couponList.push(...me.paging.initLoad());
        me.hasData = me.couponList.length > 0;
        me.PushChanges();
      }
    });
  }

  private onViewTypeChanges(viewType: ViewType) {
    const me = this;
    me.isSmallView = (viewType === ViewType.Small);
  }

  public navigateBack(): void {
    const me = this;
    me._routerHelperService.navigateBackSimple();
  }

  public navigateToCouponDetail(couponId: number | string): void {
    const me = this;
    me._router.navigate(["/coupondetail", couponId]);
  }

  /**add user's likes to filterFunction base on user's likes  and current location*/
  private async initializeData() {
    const me = this;
    try {
      const locationIDs = await me._likeService.GetContentIDForActiveLikes(ContentType.Location);
      if (locationIDs && locationIDs.length > 0) {
        me.locationFilterIds = locationIDs;
      }
      if (me._currentLocation) {
        me.locationFilterIds.push(me._currentLocation);
      }
      if (me.locationFilterIds && me.locationFilterIds.length > 0) {
        me.filterFunction = [];
        me.filterFunction.push(CouponComponent.couponLocationFilterFunction(me.locationFilterIds));
      }
      me.ReloadDataAndRender();
    } catch (e) {
      me._coreLogService.error("CouponComponent.initializeData", "GetContentIDForActiveLikes", e);
      me.ReloadDataAndRender();
    }
  }

  /**set sorting option and filter at the first loading of the page */
  private initializeSortAndFilterOption() {
    const me = this;
    me.sortOption.push(me.pagingHelper.createSortOption("like", SortDirection.DESC));
    me.sortOption.push(me.pagingHelper.createSortOption("momentFromDate", SortDirection.DESC));
    me.sortOption.push(me.pagingHelper.createSortOption("label", SortDirection.ASC));
    me.sortOption.push(me.pagingHelper.createSortOption("id", SortDirection.ASC));
  }

  // set subscriber for scrollbar and load data for next page when it is in the exact point.
  private setInfiniteScrollSubscriber() {
    const me = this;
    me._infiniteScrollSubscriber = me._scrollHelper.InfiniteScrollObserver().subscribe((loadNextPage) => {
      if (loadNextPage && !Utils.IsNull(me.paging)) {
        me.couponList.push(...me.paging.nextPage());
        me.PushChanges();
      }
    });
  }

  /**
    * Set the title of the page by title Data in route data
    * This data has been set in RouterHelperService by each navigation Enh Evnt
    */
  private setRouteData() {
    const me = this;
    me.pageTitle = me._activatedRoute.snapshot.data["pageTitle"];
    me._hasLikeFilter = me._activatedRoute.snapshot.data["hasLikeFilter"];
    const routeTargetData = me._activatedRoute.snapshot.data["routeTargetData"];
    if (routeTargetData && routeTargetData["targetScrollId"]) {
      me.targetScrollId = routeTargetData["targetScrollId"];
    }
  }

  private setRatingSubscriber() {
    const me = this;
    me._ratingChangesSubscriber = me._ratingService.ObserveChanges.subscribe((changedRatings) => {
      // check, is one of the rating a coupon key, then reload the view, with new data
      let couponRatingChanged = false;
      const couponKeyPrefix = ContentType.Coupon + "|";
      for (const changedRating of changedRatings) {
        if (changedRating.toString().startsWith(couponKeyPrefix)) {
          couponRatingChanged = true;
          break;
        }
      }
      if (couponRatingChanged) {
        me.ReloadDataAndRender();
      }
    });
  }

  /** get coupon data from storage */
  private ReloadDataAndRender(): void {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    me.couponService.GetCouponViewModel()
      .then(coupons => {
        if (coupons) {
          if (me._hasLikeFilter) {
            coupons = me._likeService.filterByLike(coupons);
          }
          const DefaultPageSize = 100;
          let CurrentPage = 2;
          if (!Utils.IsNull(me.paging)) {
            CurrentPage = me.paging.CurrentPage;
          }
          me.paging = new Paging(coupons, DefaultPageSize, me.sortOption, me.filterFunction);
          me.couponList = []; // clean up
          const result = me.paging.initLoad(CurrentPage);
          // if there is a targetScrollId, the page must be scrolled to top to show this item at the top of the page
          if (me.targetScrollId > 0) {
            // call next page until a viewModel with targetScrollId is found
            while (!result.some(s => s.id === me.targetScrollId)) {
              result.push(...me.paging.nextPage());
            }
            me.couponList.push(...result);
            me.hasData = me.couponList.length > 0;
            me.PushChanges();
            setTimeout(() => {
              const ele = document.getElementById("scrollTargetId-" + me.targetScrollId);
              me._scrollHelper.ScrollElementToTop(ele.offsetTop);
              // avoid auto scrolling to target again
              me.targetScrollId = 0;
            }, 200);
          } else {
            me.isLoaded = true;
            me.couponList.push(...result);
            me.hasData = me.couponList.length > 0;
            me.PushChanges();
          }
        } else {
          me.hasData = false;
          me.PushChanges();
        }
        me._overlaySpinnerService.HideLayoutSpinner();
      })
      .catch(e => {
        me._coreLogService.error("CouponComponent.ReloadDataAndRender", "GetCouponViewModel", e);
        me._overlaySpinnerService.HideLayoutSpinner();
        me._snackBarService.ShowErrorMessageFailed(25);
      });
  }


  public compareDateBefore(coupon: any): boolean {
    const startDate = new Date(coupon.startDate.split('.').reverse().join('-'));
    const endDate = new Date(coupon.endDate.split('.').reverse().join('-'));
    return startDate < endDate;
  }

  public compareDateEqual(coupon: any): boolean {
    const startDate = new Date(coupon.startDate.split('.').reverse().join('-'));
    const endDate = new Date(coupon.endDate.split('.').reverse().join('-'));
    return startDate == endDate;
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._langSubscriber) {
      me._langSubscriber.unsubscribe();
    }
    if (me._infiniteScrollSubscriber) {
      me._infiniteScrollSubscriber.unsubscribe();
    }
    if (me._couponChangesSubscriber) {
      me._couponChangesSubscriber.unsubscribe();
    }
    if (me._viewTypeSubscription) {
      me._viewTypeSubscription.unsubscribe();
    }
    if (me._ratingChangesSubscriber) {
      me._ratingChangesSubscriber.unsubscribe();
    }
  }
}
